
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { DarkModeProvider } from "@/contexts/DarkModeContext";
import { ProgressProvider } from "@/contexts/ProgressContext";
import AppLayout from "@/components/layout/AppLayout";
import SocialProfileCompletion from "@/components/auth/SocialProfileCompletion";
import ChatLayout from "@/components/layout/ChatLayout";
import DarkModeTransition from "@/components/DarkModeTransition";
import Home from "./pages/Home";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import MyProfile from "./pages/MyProfile";
import EditProfile from "./pages/EditProfile";
import CreateService from "./pages/CreateService";
import MyServices from "./pages/MyServices";
import EditService from "./pages/EditService";
import Conversations from "./pages/Conversations";
import ConversationDetail from "./pages/ConversationDetail";
import Chat from "./pages/Chat";
import Feedback from "./pages/Feedback";
import Polls from "./pages/Polls";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminServices from "./pages/AdminServices";
import AdminFeedback from "./pages/AdminFeedback";
import DiscoverServices from "./pages/DiscoverServices";
import CategoryPage from "./pages/CategoryPage";
import OfferSkills from "./pages/OfferSkills";
import HowToStart from "./pages/HowToStart";
import HowToHire from "./pages/HowToHire";
import SuccessStories from "./pages/SuccessStories";
import SafetyGuidelines from "./pages/SafetyGuidelines";
import TermsOfService from "./pages/TermsOfService";
import TaxFAQ from "./pages/TaxFAQ";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import UserProfile from "./pages/UserProfile";
import NewRequest from "./pages/NewRequest";
import Requests from "./pages/Requests";
import RequestCategoryPage from "./pages/RequestCategoryPage";
import EditRequestForm from "./components/EditRequestForm";
import ChatRooms from "./pages/ChatRooms";
import ChatRoomView from "./pages/ChatRoomView";
import Progress from "./pages/Progress";
import ScrollToTop from "./components/ScrollToTop";
import StripeTest from "./pages/StripeTest";
import StripeTestSuite from "./pages/StripeTestSuite";
import StripeDiagnostics from "./pages/StripeDiagnostics";
import ConnectOnboarding from "./pages/ConnectOnboarding";
import PayoutsSetup from "./pages/PayoutsSetup";
import Payments from "./pages/Payments";
import AdminEmails from "./pages/AdminEmails";
import AdminConversations from "./pages/AdminConversations";
import AdminConversationView from "./pages/AdminConversationView";
import AdminUserReports from "./pages/AdminUserReports";
import AdminRefunds from "./pages/AdminRefunds";
import Manifesto from "./pages/Manifesto";
import NotFound from "./pages/NotFound";
import VocabLab from "./pages/VocabLab";
import WordDrill from "./pages/WordDrill";
import GroupSort from "./pages/GroupSort";
import QuickRecall from "./pages/QuickRecall";
import { Navigate } from "react-router-dom";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ThemeProvider 
        attribute="class" 
        defaultTheme="light" 
        enableSystem={true}
        disableTransitionOnChange
      >
        <DarkModeProvider>
          <ProgressProvider>
            <TooltipProvider>
            <Toaster />
            <Sonner />
            <DarkModeTransition />
            <BrowserRouter>
            <ScrollToTop />
            <SocialProfileCompletion />
            <Routes>
            <Route path="/" element={<AppLayout><Home /></AppLayout>} />
            <Route path="/services" element={<AppLayout><Services /></AppLayout>} />
            <Route path="/services/:id" element={<AppLayout><ServiceDetail /></AppLayout>} />
            <Route path="/discover-services" element={<AppLayout><DiscoverServices /></AppLayout>} />
            <Route path="/category/:categoryId" element={<AppLayout><CategoryPage /></AppLayout>} />
            <Route path="/offer-service" element={<AppLayout><OfferSkills /></AppLayout>} />
            <Route path="/how-to-start" element={<AppLayout><HowToStart /></AppLayout>} />
            <Route path="/how-to-hire" element={<AppLayout><HowToHire /></AppLayout>} />
            <Route path="/success-stories" element={<AppLayout><SuccessStories /></AppLayout>} />
            <Route path="/safety-guidelines" element={<AppLayout><SafetyGuidelines /></AppLayout>} />
            <Route path="/terms-of-service" element={<AppLayout><TermsOfService /></AppLayout>} />
            <Route path="/privacy-policy" element={<AppLayout><PrivacyPolicy /></AppLayout>} />
            <Route path="/help/tax-faq" element={<AppLayout><TaxFAQ /></AppLayout>} />
            <Route path="/manifesto" element={<AppLayout><Manifesto /></AppLayout>} />
            <Route path="/auth" element={<AppLayout><Auth /></AppLayout>} />
            <Route path="/reset-password" element={<AppLayout><ResetPassword /></AppLayout>} />
            <Route path="/my-profile" element={<AppLayout><MyProfile /></AppLayout>} />
            <Route path="/edit-profile" element={<AppLayout><EditProfile /></AppLayout>} />
            <Route path="/create-service" element={<AppLayout><CreateService /></AppLayout>} />
            <Route path="/my-services" element={<AppLayout><MyServices /></AppLayout>} />
            <Route path="/edit-service/:id" element={<AppLayout><EditService /></AppLayout>} />
            <Route path="/edit-request/:id" element={<AppLayout><EditRequestForm /></AppLayout>} />
            <Route path="/requests" element={<AppLayout><Requests /></AppLayout>} />
            <Route path="/requests/category/:categoryId" element={<AppLayout><RequestCategoryPage /></AppLayout>} />
            <Route path="/request-service" element={<AppLayout><NewRequest /></AppLayout>} />
            <Route path="/new-request" element={<AppLayout><NewRequest /></AppLayout>} />
            <Route path="/conversations" element={<AppLayout><Conversations /></AppLayout>} />
            <Route path="/conversations/:id" element={<ChatLayout><ConversationDetail /></ChatLayout>} />
            <Route path="/chat/:conversationId" element={<ChatLayout><Chat /></ChatLayout>} />
            <Route path="/chat-rooms" element={<AppLayout><ChatRooms /></AppLayout>} />
            <Route path="/chat-rooms/:roomId" element={<ChatLayout><ChatRoomView /></ChatLayout>} />
            <Route path="/feedback" element={<AppLayout><Feedback /></AppLayout>} />
            <Route path="/polls" element={<AppLayout><Polls /></AppLayout>} />
            <Route path="/admin-dashboard" element={<AppLayout><AdminDashboard /></AppLayout>} />
            <Route path="/admin/users" element={<AppLayout><AdminUsers /></AppLayout>} />
            <Route path="/admin-services" element={<AppLayout><AdminServices /></AppLayout>} />
            <Route path="/admin-feedback" element={<AppLayout><AdminFeedback /></AppLayout>} />
            <Route path="/admin/emails" element={<AppLayout><AdminEmails /></AppLayout>} />
            <Route path="/admin/conversations" element={<AppLayout><AdminConversations /></AppLayout>} />
            <Route path="/admin/conversations/:conversationId" element={<AppLayout><AdminConversationView /></AppLayout>} />
            <Route path="/admin/user-reports" element={<AppLayout><AdminUserReports /></AppLayout>} />
            <Route path="/admin/refunds" element={<AppLayout><AdminRefunds /></AppLayout>} />
            <Route path="/progress" element={<AppLayout><Progress /></AppLayout>} />
            <Route path="/profile/:userId" element={<AppLayout><UserProfile /></AppLayout>} />
            
            <Route path="/stripe-test" element={<AppLayout><StripeTest /></AppLayout>} />
            <Route path="/stripe-test-suite" element={<AppLayout><StripeTestSuite /></AppLayout>} />
            <Route path="/admin/stripe-diagnostics" element={<AppLayout><StripeDiagnostics /></AppLayout>} />
            <Route path="/connect-onboarding" element={<ConnectOnboarding />} />
            <Route path="/payouts-setup" element={<PayoutsSetup />} />
            <Route path="/transactions" element={<AppLayout><Payments /></AppLayout>} />
            <Route path="/vocab-lab" element={<AppLayout><VocabLab /></AppLayout>} />
            <Route path="/vocab-lab/word-drill" element={<WordDrill />} />
            <Route path="/vocab-lab/group-sort" element={<GroupSort />} />
            <Route path="/vocab-lab/quick-recall" element={<QuickRecall />} />
            <Route path="/games" element={<Navigate to="/vocab-lab" replace />} />
            <Route path="/wordle" element={<Navigate to="/vocab-lab/word-drill" replace />} />
            <Route path="/connections" element={<Navigate to="/vocab-lab/group-sort" replace />} />
            <Route path="*" element={<AppLayout><NotFound /></AppLayout>} />
          </Routes>
        </BrowserRouter>
        </TooltipProvider>
          </ProgressProvider>
        </DarkModeProvider>
      </ThemeProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
